import type { TFunction } from 'i18next';
import {
  Badge,
  Box,
  chakra,
  Flex,
  HStack,
  Text,
  Input,
  Spinner,
} from '@chakra-ui/react';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import { DrawerComponent } from '@themes/aegix';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '../../contexts/SearchContext';
import {
  fetchSearch,
  resolveSearchRoute,
  SearchGroup,
  SearchItem,
} from '../../services/globalSearch.service';

const SEARCH_DEBOUNCE_MS = 300;

const translateSearchLabel = (label: string, t: TFunction): string => {
  if (!label) return '';
  const keyCandidate = label.toLowerCase().replace(/[^a-z0-9]/g, '');
  return t(
    `search.labels.${keyCandidate}`,
    t(`common.${keyCandidate}`, t(`nav.${keyCandidate}`, t(label, label))),
  );
};

export function GlobalSearchDrawer() {
  const { t } = useTranslation();
  const { isSearchOpen, closeSearch } = useSearch();
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handle = setTimeout(() => setQuery(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [inputValue]);

  const payload = {
    query,
    limit: 8,
    offset: 0,
  };
  const { data, isLoading } = useQuery({
    queryKey: ['global-search', query],
    queryFn: () => fetchSearch(payload),
    enabled: !!query.trim(),
    staleTime: 1000 * 60, // cache for 1 min
  });
  const searchResults = data?.results ?? [];
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setQuery(inputValue);
  };

  const handleItemClick = (
    group: SearchGroup,
    itemId: number,
    publicId?: string,
  ) => {
    const { path, tab, subTab, id } = resolveSearchRoute(group, itemId);
    const url = `${path}?tab=${tab}&id=${id}${publicId ? `&public_id=${publicId}` : ''}${subTab ? `&subtab=${subTab}` : ''}`;
    navigate(url);
    closeSearch();
  };

  return (
    <DrawerComponent
      isOpen={isSearchOpen}
      onClose={closeSearch}
      size="md"
      title={t('search.globalSearch', 'Global Search')}
    >
      <Box display="flex" flexDirection="column" gap={4} h="100%">
        {/* Search input */}
        <form onSubmit={handleSearch}>
          <HStack
            bg="surface.secondary"
            borderWidth="1px"
            borderColor="border.line"
            borderRadius="10px"
            px={3}
            py={2.5}
            gap={2.5}
            w="100%"
          >
            <HiMagnifyingGlass
              size={18}
              color="var(--chakra-colors-text-secondary, #888)"
            />
            <Input
              placeholder={t('search.placeholder', 'Search anything…')}
              aria-label={t('search.ariaLabel', 'Search anything')}
              fontSize="14px"
              color="text.primary"
              border="none"
              outline="none"
              bg="transparent"
              name="search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              p={0}
              h="auto"
              _placeholder={{ color: 'text.secondary' }}
              _focus={{ boxShadow: 'none' }}
              _focusVisible={{ outline: 'none' }}
            />
          </HStack>
        </form>

        {/* Results grouped by menu then by page */}
        <Box
          overflowY="auto"
          flex={1}
          display="flex"
          flexDirection="column"
          gap={5}
        >
          {isLoading && (
            <Flex justify="center" align="center" py={8}>
              <Spinner size="md" color="text.action" />
            </Flex>
          )}
          {!isLoading && query.trim() && searchResults.length === 0 && (
            <Flex justify="center" align="center" py={8}>
              <Text fontSize="14px" color="text.secondary">
                {t('search.noResults', 'No results found for "{{query}}"', {
                  query,
                })}
              </Text>
            </Flex>
          )}
          {!isLoading &&
            searchResults.length > 0 &&
            (() => {
              // Group by menu
              const byMenu: Record<string, SearchGroup[]> = {};
              searchResults.forEach((group) => {
                if (!byMenu[group.menu]) byMenu[group.menu] = [];
                byMenu[group.menu].push(group);
              });

              return Object.entries(byMenu).map(
                ([menu, groups]: [string, SearchGroup[]]) => (
                  <Box key={menu}>
                    {/* Menu heading */}
                    <Text
                      fontSize="11px"
                      fontWeight={700}
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      color="text.secondary"
                      mb={2}
                      px={1}
                    >
                      {translateSearchLabel(menu, t)}
                    </Text>

                    <Box display="flex" flexDirection="column" gap={3}>
                      {groups.map((group: SearchGroup) => {
                        const pageLabel = group.sub_page
                          ? `${translateSearchLabel(group.page, t)} › ${translateSearchLabel(group.sub_page, t)}`
                          : translateSearchLabel(group.page, t);

                        return (
                          <Box
                            key={group.key}
                            borderWidth="1px"
                            borderColor="border.line"
                            borderRadius="10px"
                            overflow="hidden"
                          >
                            {/* Section header */}
                            <HStack
                              px={3}
                              py={2}
                              bg="surface.secondary"
                              borderBottomWidth="1px"
                              borderColor="border.line"
                              justify="space-between"
                            >
                              <Text
                                fontSize="13px"
                                fontWeight={600}
                                color="text.primary"
                              >
                                {pageLabel}
                              </Text>
                              <Badge
                                bg="surface.tertiary"
                                color="text.secondary"
                                fontSize="11px"
                                borderRadius="full"
                                px={2}
                                py={0.5}
                              >
                                {group.count}
                              </Badge>
                            </HStack>

                            {/* Items */}
                            <Box>
                              {group.items.map(
                                (item: SearchItem, idx: number) => (
                                  // A real <button> so results are
                                  // keyboard-reachable (Tab + Enter/Space)
                                  // and expose button semantics to AT.
                                  <chakra.button
                                    key={item.id}
                                    type="button"
                                    display="flex"
                                    alignItems="center"
                                    w="100%"
                                    textAlign="left"
                                    px={3}
                                    py={2.5}
                                    gap={3}
                                    borderTopWidth={idx === 0 ? '0' : '1px'}
                                    borderColor="border.line"
                                    _hover={{
                                      bg: 'surface.secondary',
                                      cursor: 'pointer',
                                    }}
                                    transition="background 0.15s"
                                    onClick={() =>
                                      handleItemClick(
                                        group,
                                        item.id,
                                        item.public_id ?? undefined,
                                      )
                                    }
                                  >
                                    {/* Avatar-like initial */}
                                    <Flex
                                      w="32px"
                                      h="32px"
                                      borderRadius="8px"
                                      bg="surface.tertiary"
                                      borderWidth="1px"
                                      borderColor="border.secondary"
                                      align="center"
                                      justify="center"
                                      flexShrink={0}
                                    >
                                      <Text
                                        fontSize="12px"
                                        fontWeight={700}
                                        color="text.action"
                                      >
                                        {item.name.charAt(0).toUpperCase()}
                                      </Text>
                                    </Flex>

                                    <Box flex={1} minW={0}>
                                      <Text
                                        fontSize="13px"
                                        fontWeight={500}
                                        color="text.primary"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        whiteSpace="nowrap"
                                      >
                                        {item.name}
                                      </Text>
                                      {item.description && (
                                        <Text
                                          fontSize="12px"
                                          color="text.secondary"
                                          overflow="hidden"
                                          textOverflow="ellipsis"
                                          whiteSpace="nowrap"
                                        >
                                          {item.description}
                                        </Text>
                                      )}
                                    </Box>
                                  </chakra.button>
                                ),
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ),
              );
            })()}
        </Box>
      </Box>
    </DrawerComponent>
  );
}
